import React, { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TenantProvider } from '@/shared/providers/TenantProvider'
import { AuthProvider } from '@/shared/providers/AuthProvider'
import { UserPreferencesProvider } from '@/shared/providers/UserPreferencesProvider'
import { RuntimeConfigProvider } from '@/shared/providers/RuntimeConfigProvider'
import { ThemeProvider } from '@/shared/providers/ThemeProvider'
import { AppearanceProvider } from '@/shared/providers/AppearanceProvider'
import { GoogleMapsProvider } from '@/shared/providers/GoogleMapsProvider'
import { ToastProvider } from '@/shared/contexts/ToastContext'
import { UnsavedChangesProvider } from '@/shared/contexts/UnsavedChangesContext'
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary'
import { ErrorState } from '@/shared/ui/states/ErrorState'
import { router } from '@/app/router'

// Create query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <ErrorBoundary fallback={ErrorState}>
      <QueryClientProvider client={queryClient}>
        <TenantProvider>
          <AuthProvider>
            <UserPreferencesProvider>
              <RuntimeConfigProvider>
                <ThemeProvider>
                  <AppearanceProvider>
                    <GoogleMapsProvider>
                      <ToastProvider>
                        <UnsavedChangesProvider>
                          <RouterProvider router={router} />
                        </UnsavedChangesProvider>
                      </ToastProvider>
                    </GoogleMapsProvider>
                  </AppearanceProvider>
                </ThemeProvider>
              </RuntimeConfigProvider>
            </UserPreferencesProvider>
          </AuthProvider>
        </TenantProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
